import React, { useState, useEffect } from 'react';
import api from "../lib/api";
import { toast } from 'sonner';
import { FiLayers, FiPlus, FiTrash2, FiEdit } from 'react-icons/fi';

const PRE_BUILT = [
  { type: 'RSI', name: 'RSI Oversold/Overbought', params: { period: 14, oversold: 30, overbought: 70 } },
  { type: 'MACD', name: 'MACD Crossover', params: { fast: 12, slow: 26, signal: 9 } },
  { type: 'EMA_CROSSOVER', name: 'EMA Crossover', params: { fast_period: 9, slow_period: 21 } },
  { type: 'BOLLINGER_BANDS', name: 'Bollinger Bands', params: { period: 20, std_dev: 2 } },
];

const STRATEGY_TEMPLATES = {
  RSI: { period: 14, oversold: 30, overbought: 70 },
  MACD: { fast: 12, slow: 26, signal: 9 },
  EMA_CROSSOVER: { fast_period: 9, slow_period: 21 },
  BOLLINGER_BANDS: { period: 20, std_dev: 2 },
  CUSTOM: { description: 'Define your own parameters' }
};

export default function StrategyManager() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'RSI',
    parameters: JSON.stringify(STRATEGY_TEMPLATES.RSI, null, 2)
  });

  useEffect(() => {
    fetchStrategies();
  }, []);

  const fetchStrategies = async () => {
    try {
      const res = await api.get('/strategies');
      setStrategies(res.data);
    } catch (error) {
      toast.error('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (type) => {
    setFormData({
      ...formData,
      type,
      parameters: JSON.stringify(STRATEGY_TEMPLATES[type] || {}, null, 2)
    });
  };

  const handleCreateStrategy = async (e) => {
    e.preventDefault();
    try {
      const params = JSON.parse(formData.parameters);
      await api.post('/strategies', {
        name: formData.name,
        type: formData.type,
        parameters: params
      });
      toast.success('Custom strategy created successfully!');
      setShowCreateDialog(false);
      fetchStrategies();
      setFormData({
        name: '',
        type: 'RSI',
        parameters: JSON.stringify(STRATEGY_TEMPLATES.RSI, null, 2)
      });
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format in parameters');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create strategy');
      }
    }
  };

  const deleteStrategy = async (strategyId) => {
    if (!window.confirm('Delete this custom strategy?')) return;
    try {
      await api.delete(`/strategies/${strategyId}`);
      toast.success('Strategy deleted');
      fetchStrategies();
    } catch (error) {
      toast.error('Failed to delete strategy');
    }
  };

  if (loading) return <div className="text-text-muted">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold font-heading text-text-main">STRATEGY MANAGER</h1>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase px-6 py-3 rounded-sm shadow-lg"
        >
          <FiPlus className="inline mr-2" /> CREATE CUSTOM
        </button>
      </div>

      <div className="bg-surface border border-border/40 rounded-sm p-6">
        <h2 className="text-xl font-bold uppercase font-heading mb-4">Pre-Built Strategies</h2>
        <p className="text-sm text-text-muted mb-4">These strategies are ready to use. You can create custom variations below.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PRE_BUILT.map(strategy => (
            <div key={strategy.type} className="p-4 bg-surface-highlight border border-border/40 rounded-sm hover:border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <FiLayers className="text-primary" />
                <h3 className="text-lg font-bold text-text-main">{strategy.name}</h3>
              </div>
              <div className="text-xs text-text-muted space-y-1">
                <p>Type: <span className="text-primary font-mono">{strategy.type}</span></p>
                {Object.entries(strategy.params).map(([key, value]) => (
                  <p key={key}>{key}: <span className="text-text-main font-mono">{value}</span></p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-surface border border-border/40 rounded-sm p-6">
        <h2 className="text-xl font-bold uppercase font-heading mb-4">Your Custom Strategies</h2>
        {strategies.filter(s => s.is_custom).length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            <FiEdit size={48} className="mx-auto mb-4 opacity-30" />
            <p className="font-medium">No custom strategies yet</p>
            <p className="text-sm mt-2">Create your own strategy with custom parameters</p>
          </div>
        ) : (
          <div className="space-y-3">
            {strategies.filter(s => s.is_custom).map(s => (
              <div key={s.strategy_id} className="flex items-center justify-between p-4 bg-surface-highlight border border-border/40 rounded-sm hover:border-primary/20">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FiLayers className="text-secondary" />
                    <h3 className="text-lg font-bold text-text-main">{s.name}</h3>
                    <span className="text-xs px-2 py-1 bg-secondary/10 text-secondary border border-secondary/20 rounded uppercase">
                      CUSTOM
                    </span>
                  </div>
                  <div className="text-xs text-text-muted space-y-1">
                    <p>Base Type: <span className="text-primary font-mono">{s.type}</span></p>
                    <details className="mt-2">
                      <summary className="cursor-pointer hover:text-text-main">View Parameters</summary>
                      <pre className="mt-2 p-2 bg-black/50 rounded text-xs overflow-x-auto">
                        {JSON.stringify(s.parameters, null, 2)}
                      </pre>
                    </details>
                  </div>
                </div>
                <button
                  onClick={() => deleteStrategy(s.strategy_id)}
                  className="bg-destructive/10 text-destructive border border-destructive/50 hover:bg-destructive/20 px-3 py-2 rounded-sm"
                >
                  <FiTrash2 />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Strategy Examples & Guide */}
      <div className="bg-accent-orange/10 border border-accent-orange/20 rounded-sm p-6">
        <h3 className="text-lg font-bold text-accent-orange mb-3">💡 Strategy Customization Guide</h3>
        <div className="text-sm text-text-muted space-y-2">
          <p><strong className="text-text-main">RSI Strategy:</strong> Adjust oversold/overbought levels (e.g., 25/75 for more aggressive, 35/65 for conservative)</p>
          <p><strong className="text-text-main">MACD Strategy:</strong> Modify fast/slow/signal periods for different timeframes</p>
          <p><strong className="text-text-main">EMA Crossover:</strong> Change fast/slow periods (e.g., 5/15 for scalping, 20/50 for swing)</p>
          <p><strong className="text-text-main">Bollinger Bands:</strong> Adjust period and standard deviation for volatility sensitivity</p>
        </div>
      </div>

      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowCreateDialog(false)}>
          <div className="bg-surface border border-border max-w-2xl w-full rounded-sm p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold font-heading text-primary mb-6">Create Custom Strategy</h2>
            <form onSubmit={handleCreateStrategy} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-text-main">Strategy Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                  placeholder="e.g., Aggressive RSI, Fast MACD"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-text-main">Base Strategy Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-border text-text-main rounded-sm"
                >
                  <option value="RSI">RSI (Relative Strength Index)</option>
                  <option value="MACD">MACD (Moving Average Convergence Divergence)</option>
                  <option value="EMA_CROSSOVER">EMA Crossover</option>
                  <option value="BOLLINGER_BANDS">Bollinger Bands</option>
                  <option value="CUSTOM">Custom (Advanced)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-text-main">
                  Parameters (JSON Format)
                  <span className="text-xs text-text-muted ml-2">- Edit values below</span>
                </label>
                <textarea
                  value={formData.parameters}
                  onChange={(e) => setFormData({ ...formData, parameters: e.target.value })}
                  className="w-full p-4 bg-black/50 border border-border text-text-main rounded-sm font-mono text-sm"
                  rows={8}
                  placeholder='{"period": 14, "oversold": 30, "overbought": 70}'
                />
                <p className="text-xs text-text-muted mt-1">
                  Must be valid JSON format. Use the template above or modify it.
                </p>
              </div>

              {/* Parameter Guide */}
              <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
                <p className="text-xs font-bold text-primary mb-2">QUICK GUIDE FOR {formData.type}:</p>
                <div className="text-xs text-text-muted space-y-1">
                  {formData.type === 'RSI' && (
                    <>
                      <p>• <strong>period:</strong> Number of candles (default: 14)</p>
                      <p>• <strong>oversold:</strong> Buy threshold (lower = more trades, default: 30)</p>
                      <p>• <strong>overbought:</strong> Sell threshold (higher = more trades, default: 70)</p>
                      <p className="text-accent-orange mt-2">Example: {"{"}"period": 10, "oversold": 25, "overbought": 75{"}"}</p>
                    </>
                  )}
                  {formData.type === 'MACD' && (
                    <>
                      <p>• <strong>fast:</strong> Fast EMA period (default: 12)</p>
                      <p>• <strong>slow:</strong> Slow EMA period (default: 26)</p>
                      <p>• <strong>signal:</strong> Signal line period (default: 9)</p>
                      <p className="text-accent-orange mt-2">Example: {"{"}"fast": 8, "slow": 21, "signal": 7{"}"}</p>
                    </>
                  )}
                  {formData.type === 'EMA_CROSSOVER' && (
                    <>
                      <p>• <strong>fast_period:</strong> Fast EMA (default: 9)</p>
                      <p>• <strong>slow_period:</strong> Slow EMA (default: 21)</p>
                      <p className="text-accent-orange mt-2">Example: {"{"}"fast_period": 5, "slow_period": 15{"}"}</p>
                    </>
                  )}
                  {formData.type === 'BOLLINGER_BANDS' && (
                    <>
                      <p>• <strong>period:</strong> Moving average period (default: 20)</p>
                      <p>• <strong>std_dev:</strong> Standard deviation multiplier (default: 2)</p>
                      <p className="text-accent-orange mt-2">Example: {"{"}"period": 15, "std_dev": 2.5{"}"}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase py-2 rounded-sm"
                >
                  CREATE STRATEGY
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateDialog(false)}
                  className="px-6 bg-border text-text-main hover:bg-border/80 font-bold uppercase py-2 rounded-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
